/* eslint-disable react/display-name */
import {
	DirectionalHint,
	LinkCompat as FluentLink,
	FontIconCompat as FontIcon,
	HoverCardCompat as HoverCard,
	HoverCardType,
	IColumn,
	IImageStyles,
	IPersonaProps,
	IPlainCardProps,
	IStackStyles,
	ITextStyles,
	ITooltipProps,
	ImageCompat as Image,
	LabelCompat as Label,
	PersonaCompat as Persona,
	PersonaSize,
	SeparatorCompat as Separator,
	SpinnerCompat as Spinner,
	StackCompat as Stack,
	TextCompat as Text,
	Theme,
	TooltipDelay,
	TooltipHostCompat as TooltipHost,
	mergeStyleSets,
	useTheme,
} from 'components/FluentUICompat';
import {useBoolean, useId} from '@fluentui/react-hooks';
import {RichtextDisplay} from 'components/RichtextDisplay';
import {ErrorModal} from 'components/ErrorModal';
import {HierarchyListIndicator} from 'components/HieraryListIndicator';
import {DateTimeFormat, formatDateTime} from 'i18n/localeDateFormat';
import {i18n} from 'i18next';
import React, {useMemo} from 'react';
import {TFunction, useTranslation} from 'react-i18next';
import 'react-quill/dist/quill.bubble.css';
import {Link, useNavigate} from 'react-router-dom';
import {
	AttachmentCategory,
	AttachmentRef,
	BlobRef,
	DocumentReference,
	DocumentStatus,
	Keyword,
	NamedEntity,
	ParagraphElementType,
	PhaseDate,
	PogisDate,
	RegulatoryDocument,
	RegulatoryDocumentParagraph,
	RegulatoryDocumentParagraphElement,
	Requirement,
	RequirementChangeType,
	TextPosition,
	TranslationObject,
	User,
	VexCluster,
} from 'types';
import {v4 as uuidv4} from 'uuid';
import {Personas, VexesPersona, VkosPersona} from 'components/Personas';
import {VexClusterOfVexClustersField} from './RequirementsFields/VexClustersField/VexClusterItem';
import {TableComponent} from 'components/TableComponent';
import DOMPurify from 'dompurify';
import {DEFAULT_DATE} from './EntityUtils';
import {FictiousSegment} from '../../features/RegulatoryDocuments/components/DocumentDetails/FictiousSegmentDetails/FictiousSegment';
import {FaqComponent} from 'components/Faq/FaqComponent';
import {RequirementsField} from './RequirementField';
import {getDisassemblyStatusTag, getStatusTag} from '../StatusTag';
import {GetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';
import {HtmlComponent} from 'components/HtmlComponent/HtmlComponent';
import {CellWithEntityLink} from './CellWithEntityLink';

export type FieldRenderInput = {
	item: any;
	i?: number;
	col?: IColumn;
};

export type LazyFieldRenderInput = FieldRenderInput & {isLazy: boolean};

export type RenderFn = (
	item?: any,
	index?: number | undefined,
	column?: IColumn | undefined,
) => JSX.Element;

const getFieldValue = (
	item: any,
	_: number | undefined,
	col: IColumn | undefined,
) => {
	if (col?.fieldName) {
		return item[col.fieldName];
	}

	return null;
};

export type RichTextValue = string | null;

export const renderRichtext: (
	limit?: number,
	removeHtml?: boolean,
) => RenderFn =
	(limit = 400, removeHtml = false) =>
	(item, i, col) => {
		const fieldValue: RichTextValue = getFieldValue(item, i, col);

		if (fieldValue) {
			return (
				<RichtextDisplay
					value={fieldValue}
					limit={limit}
					removeHtml={removeHtml}
				/>
			);
		}

		return <></>;
	};

export type ValueForPersonaColumn = User | null;

export const renderPersona: (props?: Partial<IPersonaProps>) => RenderFn =
	props => (item, i, col) => {
		const fieldValue: ValueForPersonaColumn = getFieldValue(item, i, col);

		if (fieldValue) {
			return <Persona text={fieldValue.name} {...props} />;
		}

		return <></>;
	};

export const renderPersonas: (props?: Partial<IPersonaProps>) => RenderFn =
	props => (item, i, col) => {
		const fieldValue: User[] | null = getFieldValue(item, i, col);

		return (
			<div>
				{fieldValue ? <Personas persons={fieldValue} {...props} /> : <></>}
			</div>
		);
	};

export const renderVkosPersona: (
	keywords: Keyword[],
	dataVkos: GetAllVkosQuery | undefined,
	propsPersona?: Partial<IPersonaProps>,
) => RenderFn = (keywords, dataVkos, propsPersona) => (item, i, col) => {
	const users: User[] = getFieldValue(item, i, col) ?? [];
	const propsVkoPersonas = {users, keywords, dataVkos, propsPersona};
	return <VkosPersona {...propsVkoPersonas} />;
};

export const renderVexesPersona: (
	keywords: Keyword[],
	propsPersona?: Partial<IPersonaProps>,
) => RenderFn = (keywords, propsPersona) => (item, i, col) => {
	const users: User[] = getFieldValue(item, i, col) ?? [];
	const propsVexesPersonas = {users, keywords, propsPersona};
	return <VexesPersona {...propsVexesPersonas} />;
};

export type ArrayValueProps<T> = {
	array: T[];
	getValue?: (arrayItem: T) => any;
	limit?: number;
	getTextStyles?: (arrayItem: T) => ITextStyles | undefined;
	hideTitle?: boolean;
	forceColumnFlow?: boolean;
};

export interface ArrayItemWithName {
	name: string;
}

/**
 * @param array This can be any type of array. If each item in the array has a
 * "name" field, this component will render it automatically.
 */
export function ArrayValues<Item>({
	array,
	getValue,
	limit,
	getTextStyles,
	hideTitle,
	forceColumnFlow,
}: ArrayValueProps<Item>) {
	const theme = useTheme();

	const classNames = mergeStyleSets({
		arrayWrapper: {
			display: 'flex !important',
			flexFlow: forceColumnFlow === true ? 'column wrap' : 'wrap',
			alignItems: forceColumnFlow === true ? 'center' : 'normal',
		},
		arrayItem: {
			borderRadius: '10px',
			color: theme?.palette.neutralSecondary,
			background: theme?.palette.neutralLight,
			fontSize: 12,
			padding: '2px 7px',
			marginRight: 5,
			marginBottom: 10,
			whiteSpace: 'nowrap',
		},
		arrayButton: {
			cursor: 'pointer',
		},
	});

	const [showAll, setShowAll] = React.useState(limit === undefined);
	const truncatedArray = React.useMemo(
		() => array.slice(0, limit),
		[array, limit],
	);
	const diff = React.useMemo(() => array.length - (limit ?? 0), [array, limit]);

	const renderValues = (arr: Item[]) =>
		arr.map(arrayItem => {
			const displayValue =
				getValue?.(arrayItem) ?? ((arrayItem as any)?.name || arrayItem);

			let title;
			if (hideTitle === false && typeof displayValue === 'string') {
				title = displayValue;
			}

			return displayValue ? (
				<Text
					key={uuidv4()}
					className={classNames.arrayItem}
					styles={getTextStyles?.(arrayItem)}
					title={title}
				>
					{displayValue}
				</Text>
			) : (
				<></>
			);
		});
	if (limit === undefined || limit >= array.length) {
		return <div className={classNames.arrayWrapper}>{renderValues(array)}</div>;
	}

	return (
		<div className={classNames.arrayWrapper}>
			{showAll ? (
				<div className={classNames.arrayWrapper}>{renderValues(array)}</div>
			) : (
				<div className={classNames.arrayWrapper}>
					{renderValues(truncatedArray)}
					<Text
						onClick={() => setShowAll(true)}
						className={`${classNames.arrayItem} ${classNames.arrayButton}`}
					>{`+ ${diff}`}</Text>
				</div>
			)}
		</div>
	);
}
export const renderDateString: () => RenderFn = () => (item, i, col) => {
	const fieldValue = getFieldValue(item, i, col);
	const {i18n} = useTranslation();

	const date =
		fieldValue && fieldValue !== DEFAULT_DATE
			? formatDateTime(new Date(fieldValue), i18n)
			: '';
	return <div>{`${date}`}</div>;
};

export const renderDateStringOrDefaultDate: () => RenderFn =
	() => (item, i, col) => {
		const fieldValue = getFieldValue(item, i, col);
		const {i18n} = useTranslation();

		const date =
			fieldValue && fieldValue !== DEFAULT_DATE
				? formatDateTime(new Date(fieldValue), i18n)
				: formatDateTime(new Date(DEFAULT_DATE), i18n);
		return <div>{`${date}`}</div>;
	};

export function renderArrayField<T>(
	getValue?: (arrayItem: T) => any,
	limit?: number,
	getTextStyles?: (arrayItem: T) => ITextStyles | undefined,
): RenderFn {
	return (item, i, col) => {
		const fieldValue: T[] | null = getFieldValue(item, i, col);

		if (!Array.isArray(fieldValue)) {
			return <></>;
		}

		return (
			<ArrayValues
				array={fieldValue}
				getValue={getValue}
				getTextStyles={getTextStyles}
				limit={limit ?? 4}
			/>
		);
	};
}

export function renderTranslationField<T>(): RenderFn {
	return (item, i, col) => {
		const entityTranslations = item.translations as TranslationObject[];

		return (
			<table>
				<tr>
					<td>default:</td>
					<td>{item.name}</td>
				</tr>
				{(entityTranslations ?? []).map((pair, i) => (
					<tr key={i}>
						<td>{pair.lang}:</td>
						<td>{pair.translation}</td>
					</tr>
				))}
			</table>
		);
	};
}

const PhaseField: React.FC<FieldRenderInput> = ({item, i, col}) => {
	const {i18n} = useTranslation();

	return renderArrayField((phase: PhaseDate) => {
		return phase.status
			? `${getDateString(i18n, phase.date)} (${phase.status})`
			: `${getDateString(i18n, phase.date)}`;
	})(item, i, col);
};

export const renderPhase: () => RenderFn = () => (item, i, col) =>
	<PhaseField {...{item, i, col}} />;

export type RequirementWithoutDetails = Pick<Requirement, 'id' | 'name'>;
export interface RequirementOfRequirementsField
	extends Pick<
		Requirement,
		'id' | 'status' | 'name' | 'requirementId' | 'definition'
	> {
	vexClusters: VexClusterOfVexClustersField[];
}

export const renderRequirements: () => RenderFn = () => (item, i, col) =>
	<RequirementsField {...{item, i, col, isLazy: false}} />;

export const renderLazyRequirements: () => RenderFn = () => (item, i, col) =>
	<RequirementsField {...{item, i, col, isLazy: true}} />;

export const renderBoolean: () => RenderFn = () => (item, i, col) => {
	const fieldValue = getFieldValue(item, i, col);

	return fieldValue ? (
		<FontIcon iconName='CheckMark' style={{color: 'green'}} />
	) : (
		<FontIcon iconName='Cancel' style={{color: 'red'}} />
	);
};

export const renderDeleted: () => RenderFn = () => (item, i, col) => {
	const fieldValue = getFieldValue(item, i, col);

	return fieldValue ? (
		<FontIcon iconName='RemoveFromShoppingList' style={{color: 'red'}} />
	) : (
		<FontIcon iconName='AddToShoppingList' style={{color: 'green'}} />
	);
};

export const getDateString = (
	i18n: i18n,
	fieldValue: Date | null,
	format?: DateTimeFormat,
) => {
	if (fieldValue instanceof Date && !isNaN(fieldValue as any)) {
		return formatDateTime(fieldValue, i18n, format);
	}

	if (typeof fieldValue === 'string') {
		return formatDateTime(new Date(fieldValue), i18n, format);
	}

	return '';
};

export type ValueForDateColumn = Date | null;

/**
 * By default, this requires setting a fieldName to get the item's value.
 */
const DateField: React.FC<FieldRenderInput & {format?: DateTimeFormat}> = ({
	item,
	i,
	col,
	format,
}) => {
	const {i18n} = useTranslation();

	const fieldValue: ValueForDateColumn = getFieldValue(item, i, col);

	return <span>{getDateString(i18n, fieldValue, format)}</span>;
};

export const renderDate: (format?: DateTimeFormat) => RenderFn =
	format => (item, i, col) =>
		<DateField {...{item, i, col, format}} />;

const PogisDateField: React.FC<
	FieldRenderInput & {format?: DateTimeFormat}
> = ({item, i, col, format}) => {
	const {i18n} = useTranslation();
	return renderArrayField(
		(pogisDate: PogisDate) =>
			`${getDateString(i18n, pogisDate.date, format)} (${pogisDate.dateType})`,
	)(item, i, col);
};

export const renderPogisDate: (format?: DateTimeFormat) => RenderFn =
	format => (item, i, col) =>
		<PogisDateField {...{item, i, col, format}} />;

type BlobRefWithOptionalFileName = {
	uri: BlobRef['uri'];
	fileName?: BlobRef['fileName'];
};
export interface FieldsForAttachmentColumn
	extends Pick<AttachmentRef, 'attachmentId'> {
	file: BlobRefWithOptionalFileName;
	category?: Pick<AttachmentCategory, 'name'> | null;
}

export type ValueForAttachmentColumn =
	| FieldsForAttachmentColumn[]
	| null
	| undefined;

const AttachmentField: React.FC<FieldRenderInput> = ({item, i, col}) => {
	const fieldValue: ValueForAttachmentColumn = getFieldValue(item, i, col);
	const {t} = useTranslation('components/columnrenderers', {
		keyPrefix: 'Attachments',
	});
	const theme = useTheme();

	return attachmentField({fieldValue, i, t, theme});
};

const AttachmentFieldPogis: React.FC<FieldRenderInput> = ({item, i, col}) => {
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});
	const theme = useTheme();
	const regex = /POGIS\sSummary\s\d+_\d+\.pdf/g;

	const summaryAttachments: AttachmentRef[] = item?.pogisAttachments.filter(
		(x: any) => {
			return x?.file.fileName.match(regex);
		},
	);

	const otherAttachments: AttachmentRef[] = item?.pogisAttachments.filter(
		(x: any) => {
			return !x?.file.fileName.match(regex);
		},
	);

	const getAttachSection = (name: string, attachment: AttachmentRef[]) => (
		<>
			<b>{name}:</b>
			<div style={{marginLeft: 3, marginTop: 3, marginBottom: 6}}>
				{attachment.map((attachment, i) => (
					<Stack horizontal key={i} tokens={{childrenGap: 6}}>
						<FluentLink
							target='_blank'
							download={false}
							href={attachment.file.uri}
							theme={theme}
						>
							<FontIcon iconName='Attach' />
							{attachment.attachmentId}
						</FluentLink>
					</Stack>
				))}
			</div>
		</>
	);

	const {compSummarySection, compOtherSection} = useMemo(() => {
		const compSummarySection = getAttachSection(
			t('SummaryAttach'),
			summaryAttachments,
		);
		const compOtherSection = getAttachSection(
			t('OtherAttach'),
			otherAttachments,
		);
		return {
			compSummarySection,
			compOtherSection,
		};
	}, [item]);

	return (
		<Stack>
			{compSummarySection}
			{compOtherSection}
		</Stack>
	);
};
const attachmentField: React.FC<{
	fieldValue: ValueForAttachmentColumn | any;
	i: number | undefined;
	t: TFunction;
	theme: Theme;
}> = ({fieldValue, i, t, theme}) => {
	return (
		<AttachmentFieldComponent
			fieldValue={fieldValue}
			i={i}
			t={t}
			theme={theme}
		/>
	);
};

export const AttachmentFieldComponent: React.FC<{
	fieldValue: ValueForAttachmentColumn | any;
	i: number | undefined;
	t: TFunction;
	theme: Theme;
}> = ({fieldValue, i, t, theme}) => {
	return fieldValue && fieldValue.length > 0 ? (
		<TooltipHost
			key={`${i}`}
			tooltipProps={{
				onRenderContent: () => (
					<Stack>
						<Text variant='medium'>{t('AttachmentsTooltipHeader')}</Text>
						{(fieldValue || []).map((attRef: any, i: number) => (
							<Stack.Item key={i}>
								<Stack horizontal key={i} tokens={{childrenGap: 6}}>
									<FluentLink
										target='_blank'
										download={false}
										href={
											attRef.file.originalFileName
												? `${attRef.file.uri}/${attRef.file.originalFileName}`
												: `${attRef.file.uri}`
										}
									>
										{attRef.file.originalFileName
											? attRef.file.originalFileName
											: attRef.attachmentId}
									</FluentLink>
									{attRef.category && (
										<Stack.Item
											style={{whiteSpace: 'nowrap'}}
										>{`(${attRef.category?.name})`}</Stack.Item>
									)}
								</Stack>
							</Stack.Item>
						))}
					</Stack>
				),
			}}
		>
			<FontIcon
				style={{fontSize: theme.fonts.mediumPlus.fontSize || undefined}}
				iconName='Attach'
			/>
		</TooltipHost>
	) : (
		<></>
	);
};

const LinkField: React.FC<FieldRenderInput> = ({item}) => {
	const navigate = useNavigate();

	return item?.linkVersion ? (
		<div
			onClick={() => navigate(`/${item?.linkVersion}`)}
			style={{cursor: 'pointer'}}
		>
			{item?.linkVersion}
		</div>
	) : (
		<></>
	);
};

export const renderLinkField: () => RenderFn = () => (item, i, col) =>
	<LinkField {...{item, i, col}} />;

export const renderAttachments: () => RenderFn = () => (item, i, col) =>
	<AttachmentField {...{item, i, col}} />;

export const renderFaqs: () => RenderFn = () => (item, i, col) => {
	if (!item.id) return <></>;
	return <FaqComponent questions={(item as any).question} entity={item} />;
};

export const renderAttachmentsPogis: () => RenderFn = () => (item, i, col) =>
	<AttachmentFieldPogis {...{item, i, col}} />;

export interface ElementFieldsForParagraphColumn
	extends Pick<
		RegulatoryDocumentParagraphElement,
		'type' | 'text' | 'isHeader' | 'textPosition'
	> {
	asset?: Pick<BlobRef, 'uri'> | null;
}

type ParagraphFieldsSetToOptional = Pick<
	Partial<RegulatoryDocumentParagraph>,
	'enumeration' | 'isFootnote' | 'level'
>;

/**
 * Note that "enumeration" and "isFootnote" are required if you want to show the
 * enumeration.
 */
export interface FieldsForParagraphColumn extends ParagraphFieldsSetToOptional {
	elements: ElementFieldsForParagraphColumn[];
}

type ParagraphFieldProps = {
	item: FieldsForParagraphColumn;
	t: TFunction;
	showEnumeration?: boolean;
};

export const ParagraphField: React.FC<ParagraphFieldProps> = ({
	item,
	t,
	showEnumeration = true,
}) => {
	const classNames = mergeStyleSets({
		labels: {
			padding: 0,
			fontSize: 16,
		},
		text: {
			whiteSpace: 'pre-wrap',
		},
	});

	const imageStyles: IImageStyles = {
		root: {zIndex: 900},
		image: {maxWidth: '100%'},
	};

	const stackStyles: IStackStyles = {
		root: {
			paddingTop: 11,
			paddingBottom: 11,
			paddingLeft: 8,
		},
	};

	const renderTextElement = (
		text: string,
		textPosition?: TextPosition | null,
	) => {
		const textContent = DOMPurify.sanitize(text);
		const textElement = (
			<span dangerouslySetInnerHTML={{__html: textContent}} />
		);

		switch (textPosition) {
			case TextPosition.Subscript:
				return <sub>{textElement}</sub>;
			case TextPosition.Superscript:
				return <sup>{textElement}</sup>;

			default:
				return <>{textElement}</>;
		}
	};

	return (
		<Stack horizontal styles={{root: {height: '100%'}}}>
			<HierarchyListIndicator level={item.level ?? 0} horizontalLineY={32} />
			<Stack styles={stackStyles}>
				{showEnumeration && (
					<Label className={classNames.labels}>
						{item.isFootnote ? t('Footnote') : item.enumeration}
					</Label>
				)}
				<Text>
					{item.elements &&
					item.elements[0]?.text?.startsWith('VKO_PDF_DISMANTLED') ? (
						<>
							<FictiousSegment text={item.elements[0].text} item={item} />
						</>
					) : (
						(item.elements || []).map((elem, i) => {
							switch (elem.type) {
								case ParagraphElementType.Text:
									if (elem.asset) {
										return (
											<Image
												key={i}
												src={elem.asset?.uri || ''}
												styles={imageStyles}
											/>
										);
									}

									return (
										<span key={i} className={classNames.text}>
											{elem.isHeader
												? ''
												: renderTextElement(elem.text || '', elem.textPosition)}
										</span>
									);
								case ParagraphElementType.Table:
								case ParagraphElementType.Image:
									return (
										<Image
											key={i}
											src={elem.asset?.uri || ''}
											styles={imageStyles}
										/>
									);
								case ParagraphElementType.NewLine:
									return (
										<div key={i}>
											<br />
										</div>
									);
								case ParagraphElementType.HtmlTable:
									return <TableComponent key={i} htmlTable={elem.text ?? ''} />;
								case ParagraphElementType.Html:
									return (
										<HtmlComponent key={i} htmlContent={elem.text ?? ''} />
									);
								default:
									return <></>;
							}
						})
					)}
				</Text>
			</Stack>
		</Stack>
	);
};

export const renderParagraph = (
	item: RegulatoryDocumentParagraph,
	t: TFunction,
	showEnumeration?: boolean,
) => <ParagraphField {...{item, t, showEnumeration}} />;

export const renderRegulatoryDocumentStatus: () => RenderFn = () =>
	renderStatus('RegulatoryDocumentStatus');

export const renderRequirementStatus = (): RenderFn =>
	renderStatus('RequirementStatus');

export const renderRequirementCategory = (): RenderFn =>
	renderStatus('RequirementCategory');

export const renderRequirementChangeType = (): RenderFn =>
	renderStatus('RequirementChangeType');

export const renderChangeInfoType = (): RenderFn =>
	renderStatus('ChangeInfoType');

export const renderTransferStatus = (): RenderFn =>
	renderStatus('TransferStatus');

export interface StatusFieldProps extends FieldRenderInput {
	enumName: string;
	value?: string;
}

//TO DO - need to change the enumname to enumtype to avoid hardcoding
export const StatusField: React.FC<StatusFieldProps> = ({
	item,
	i,
	col,
	enumName,
	value,
}) => {
	const theme = useTheme();
	const {t} = useTranslation('common/enums', {
		keyPrefix: enumName,
	});
	const fieldValue: any = value ?? getFieldValue(item, i, col);
	if (
		enumName === 'RequirementChangeType' &&
		fieldValue === RequirementChangeType.None
	) {
		return <></>;
	}

	if (fieldValue) {
		return getStatusTag(fieldValue, theme, t);
	}

	return <></>;
};

export const DisassemblyStatusField: React.FC<StatusFieldProps> = ({
	item,
	i,
	col,
	enumName,
	value,
}) => {
	const theme = useTheme();
	const {t} = useTranslation('common/enums', {
		keyPrefix: enumName,
	});
	const fieldValue: any = value ?? getFieldValue(item, i, col);
	return getDisassemblyStatusTag(fieldValue, theme, t);
};

/**
 * ! Important info
 *
 * To show the correct value, the column must have a fieldName, and
 * item[fieldName] must be defined.
 */
export const renderStatus =
	(enumName: string): RenderFn =>
	(item, i, col) =>
		<StatusField {...{item, i, col, enumName}} />;

const DocumentToolTipContent: React.FC<{
	documentStatus: DocumentStatus;
	showErrorModal: () => void;
}> = ({documentStatus, showErrorModal}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'SplitDocumentLegend',
	});

	switch (documentStatus) {
		case DocumentStatus.NotSplit:
			return <div>{t('DocumentNotDismantled')}</div>;
		case DocumentStatus.SplittingInProgress:
			return <div>{t('DocumentIsDismantling')}</div>;
		case DocumentStatus.SplittingFinished:
			return <div>{t('DocumentIsDismantled')}</div>;
		case DocumentStatus.SplittingError:
			return (
				<div>
					<div>{t('DocumentError')}</div>
					<FluentLink as='button' onClick={showErrorModal}>
						Mehr anzeigen...
					</FluentLink>
				</div>
			);
		default:
			return <></>;
	}
};

const DocumentField: React.FC<FieldRenderInput> = ({item, i, col}) => {
	const theme = useTheme();
	const classNames = mergeStyleSets({
		fileIconHeaderIcon: {
			padding: 0,
			fontSize: '16px',
			color: theme.palette.neutralPrimary,
		},
		fileIconHeaderIconNotSplit: {
			padding: 0,
			fontSize: '16px',
			color: theme.palette.red,
		},
	});

	const documentStatus: DocumentStatus = getFieldValue(item, i, col);
	const {documentError} = item;
	const documentTooltipId = useId(`documentTooltip-${i}`);

	const [
		isDocumentErrorModalOpen,
		{setTrue: showDocumentErroModal, setFalse: hideDocumentErrorModal},
	] = useBoolean(false);

	let component = <></>;
	if (!documentStatus) {
		return component;
	}

	if (documentStatus === DocumentStatus.NotSplit) {
		component = (
			<FontIcon
				aria-label='Page'
				iconName='PDF'
				className={classNames.fileIconHeaderIconNotSplit}
			/>
		);
	}

	if (documentStatus === DocumentStatus.SplittingInProgress) {
		component = <Spinner />;
	}

	if (documentStatus === DocumentStatus.SplittingFinished) {
		component = (
			<FontIcon
				aria-label='Page'
				iconName='PDF'
				className={classNames.fileIconHeaderIcon}
			/>
		);
	}

	if (documentStatus === DocumentStatus.SplittingError) {
		component = (
			<FontIcon
				aria-label='Page'
				iconName='StatusErrorFull'
				className={classNames.fileIconHeaderIconNotSplit}
			/>
		);
	}

	const onRenderTooltipContent = () => (
		<DocumentToolTipContent
			documentStatus={documentStatus}
			showErrorModal={showDocumentErroModal}
		/>
	);

	const documentTooltipProps: ITooltipProps = {
		onRenderContent: onRenderTooltipContent,
	};

	return (
		<React.Fragment>
			<TooltipHost
				tooltipProps={documentTooltipProps}
				delay={TooltipDelay.zero}
				id={documentTooltipId}
				directionalHint={DirectionalHint.leftCenter}
			>
				{component}
			</TooltipHost>
			<ErrorModal
				isOpen={isDocumentErrorModalOpen}
				onDismiss={hideDocumentErrorModal}
				error={documentError}
			/>
		</React.Fragment>
	);
};

export const renderDocument: () => RenderFn = () => (item, i, col) =>
	<DocumentField {...{item, i, col}} />;

type FieldsForReferenceItem = Pick<NamedEntity, 'id' | 'name'>;

function ReferenceItem<T extends FieldsForReferenceItem>(props: {
	entities: T[];
	referenceName: string;
	getUrl: (entity: T) => string;
}) {
	const theme = useTheme();
	const {entities, referenceName, getUrl} = props;

	return (
		<React.Fragment>
			{entities.map(entity => (
				<Stack
					key={`${referenceName}-${entity.id}`}
					horizontal
					tokens={{childrenGap: 4}}
				>
					<span>{referenceName}:</span>
					<Link
						style={{
							color: theme.palette.themePrimary,
							textDecoration: 'none',
						}}
						to={getUrl(entity)}
					>
						{entity.name}
					</Link>
				</Stack>
			))}
		</React.Fragment>
	);
}

export interface ReferenceForReferenceField {
	requirementReferences?: FieldsForReferenceItem[];
	reference?: FieldsForReferenceItem | null;
	documentReferenceGroup?: {
		regulationReference?: FieldsForReferenceItem | null;
		regulatoryDocumentReferences?: FieldsForReferenceItem[] | null;
		selectAllRegulatoryDocuments?: boolean;
	};
	referencedByDocuments?: RegulatoryDocument[];
}

export const ReferenceField: React.FC<{
	references: ReferenceForReferenceField[];
}> = ({references}) => {
	return (
		<>
			{references.map((dr, i) => {
				const referenceType = dr.reference?.name;
				const group = dr.documentReferenceGroup;
				const outgoingDocs = group?.regulatoryDocumentReferences ?? [];
				const incomingDocs = dr.referencedByDocuments ?? [];
				const regulation = group?.regulationReference;
				const requirementRefs = dr.requirementReferences ?? [];

				return (
					<Stack key={i} tokens={{childrenGap: 8}}>
						<span>{referenceType}:</span>
						{regulation &&
							outgoingDocs.length > 0 &&
							outgoingDocs.map((doc, idx) => (
								<ReferenceLinkRow
									key={`doc-${i}-${idx}`}
									href={`/regulatoryDocuments/${doc.id}`}
								>
									<strong>{regulation.name ?? ''}</strong> | {doc.name}
								</ReferenceLinkRow>
							))}
						{incomingDocs.length > 0 &&
							incomingDocs.map((doc, idx) => (
								<ReferenceLinkRow
									key={`doc-in-${i}-${idx}`}
									href={`/regulatoryDocuments/${doc.id}`}
								>
									<strong>{doc.regulation?.regulationNumber ?? ''}</strong> |{' '}
									{doc.name}
								</ReferenceLinkRow>
							))}
						{/* This is a special cases for the older references in the prod,
							 regulations references without documents references*/}
						{regulation &&
							outgoingDocs.length === 0 &&
							incomingDocs.length === 0 && (
								<ReferenceLinkRow href={`/regulations/${regulation.id}`}>
									<strong>{regulation.name ?? ''}</strong>
								</ReferenceLinkRow>
							)}
						{requirementRefs.length > 0 && (
							<ReferenceItem
								entities={requirementRefs}
								referenceName={dr.reference?.name ?? ''}
								getUrl={req => `/requirements/${req.id}`}
							/>
						)}
					</Stack>
				);
			})}
		</>
	);
};

export type ValueForReferenceColumn = ReferenceForReferenceField[] | null;

export const renderReferenceField = (): RenderFn => (item, i, col) => {
	const fieldValue: ValueForReferenceColumn = getFieldValue(item, i, col);

	if (!fieldValue) {
		return <></>;
	}

	return <ReferenceField {...{references: fieldValue}} />;
};

export const renderReferenceLinkField = (): RenderFn => (item, i, col) => {
	const fieldValue: ValueForReferenceColumn = getFieldValue(item, i, col);
	if (!fieldValue) {
		return <>-</>;
	}

	const regulatoryDocumentId = item.id;

	return (
		<ReferenceLinkField
			references={fieldValue}
			regulatoryDocumentId={regulatoryDocumentId}
		/>
	);
};

export const ReferenceLinkField: React.FC<{
	references: ReferenceForReferenceField[];
	regulatoryDocumentId?: string;
}> = ({references, regulatoryDocumentId}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RegulatoryDocumentDetailsPage',
	});
	const hasOutgoing = references.some(
		dr =>
			(dr.documentReferenceGroup?.regulatoryDocumentReferences ?? []).length >
			0,
	);

	const hasIncoming = references.some(
		dr => (dr.referencedByDocuments ?? []).length > 0,
	);

	if (!hasOutgoing && !hasIncoming) {
		return <>-</>;
	}

	const href = `/regulatoryDocuments/${regulatoryDocumentId}?view=ReferenceTab`;

	return (
		<Stack horizontal verticalAlign='center' tokens={{childrenGap: 4}}>
			<Link to={href}>{t('DocumentReferenceLink')}</Link>
		</Stack>
	);
};

export const renderVexClusters: () => RenderFn = () => (item, i, col) => {
	const theme = useTheme();
	const classnames = getHoverClassNames(theme);
	const {t: tClusters} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminVexClustersPage',
	});
	const vClusters: VexCluster[] = React.useMemo(
		() => getFieldValue(item, i, col) ?? [],
		[item, i, col],
	);
	const propPersona = {
		size: PersonaSize.size8,
	};

	return (
		<>
			{vClusters.map(vc => {
				const hoverPropsDefault: IPlainCardProps = {
					onRenderPlainCard: () => (
						<Stack className={classnames.hover} tokens={{childrenGap: 5}}>
							<div>
								<Label>{`${vc.name}`}</Label>
							</div>
							<div className={classnames.detailCluster}>
								{tClusters('Vexes')}
								<Separator />
								{vc.users.map((vex, index) => (
									<Persona key={index} text={vex.name} {...propPersona} />
								))}
							</div>
						</Stack>
					),
				};
				return (
					<HoverCard
						type={HoverCardType.plain}
						plainCardProps={hoverPropsDefault}
						key={uuidv4()}
					>
						<ArrayValues array={[vc.name]} hideTitle={true}></ArrayValues>
					</HoverCard>
				);
			})}
		</>
	);
};

const ReferenceLinkRow: React.FC<{
	href: string;
	children?: React.ReactNode;
}> = ({href, children}) => (
	<Stack horizontal verticalAlign='center' tokens={{childrenGap: 4}}>
		<CellWithEntityLink href={href}>{children}</CellWithEntityLink>
	</Stack>
);

export const getHoverClassNames = (theme: Theme) =>
	mergeStyleSets({
		hover: {
			padding: 10,
			borderRadius: 5,
			boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
			minWidth: 300,
			maxWidth: 500,
			width: '25vw',
		},
		detailCluster: {
			margin: 5,
			minHeight: 80,
			border: '1px solid lightgray',
			padding: 10,
			borderRadius: 5,
			boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
		},
	});
